import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  FetchPriority,
  type GameDetails,
  type Lobby,
  type LobbyCategory,
  type LobbyGame,
  type Studio,
  TileType,
} from '../../_ui-core_/base/package/api/package/gaming/gaming-domain';
import { SubHelper } from '../../_ui-core_/base/package/patterns/package/store';
import { FeatureFlag } from '../../_ui-core_/base/package/services/package/features/feature-service';
import { I18n } from '../../_ui-core_/base/package/services/package/i18n/i18n.de';
import {
  adaptCategoriesToLayout,
  filterUserBasedCategories,
} from '../../_ui-core_/base/package/util/category-tiles-helper';
import { isDesktopWidth } from '../../_ui-core_/base/package/util/device';
import { dispatchCustomEvent } from '../../_ui-core_/base/package/util/event';
import { localStorage_getOrNull } from '../../_ui-core_/base/package/util/localStorage';
import sortProviderByGamesNumber from '../../_ui-core_/base/package/util/providers-game-number-sort';
import { idleTick, yieldToMain } from '../../_ui-core_/base/package/util/render';
import type { UserAreaGame } from '../../_ui-core_/components/ui-user-area/ui-user-area';
import { BonusStage, BonusState } from '../../_ui-core_/mod-bonus/types';
import { getGame } from '../../_ui-core_/mod-bonus/utils/bonus-utils';
import App from '../../app';
import type { ClubThousand, HeroBanner, Story } from '../../app/package/base/cms/package/cms-domain';
import { HeaderOptionsEventName } from '../../app/package/base/router/router-types/sidebar-types';
import { FRONTEND_ONLY_CATEGORY } from '../../app/package/base/service/content/content-helper';
import { type LoginObject, LoginStatus } from '../../app/package/base/service/login/login-domain';
import {
  NavigationTrigger,
  TrackableEventAction,
  TrackingEventSource,
} from '../../app/package/base/service/tracking/tracking-service';
import type { Layout } from '../../app/package/base/types';
import type { BonusStoreState } from '../../app/package/casino-de/service/bonus/bonus-de-service';
import { STORAGE_KEY_HAS_USER_LOGGED_IN_ONCE } from '../../constants';
import placeholderStyles from '../../styles/placeholder.css?inline';
import { CategoryPageId } from '../category/category-page';
import styles from './lobby-page.css?inline';
import '../../_ui-core_/components/carousels/ui-club-1000-carousel';
import '../../_ui-core_/components/carousels/ui-studios-carousel';
import '../../_ui-core_/components/ui-activity-lane/ui-activity-lane';
import '../../_ui-core_/components/ui-free-spin-available/ui-free-spin-available';
import '../../_ui-core_/components/ui-global-status-banner/ui-global-status-banner';
import '../../_ui-core_/components/ui-section-header/ui-section-header';
import '../../_ui-core_/components/ui-slider-desktop/ui-slider-desktop';
import '../../_ui-core_/components/ui-slider/ui-slider';
import '../../_ui-core_/components/ui-user-area/ui-user-area';

const CName = 'lobby-page';

/**
 * @fires restore-scroll-position - Event fired when the scroll position should be restored
 */
@customElement(CName)
export class LobbyPage extends LitElement {
  static readonly styles = [unsafeCSS(placeholderStyles), unsafeCSS(styles)];

  @state() private _banners?: HeroBanner;
  @state() private _stories?: Story[];
  @state() private _clubThousandItems: ClubThousand[] | undefined;
  @state() private _favoriteGames: LobbyGame[] = [];
  @state() private _lastPlayedGames: LobbyGame[] | undefined = undefined;
  @state() private _layout: Layout | undefined;
  @state() private _lobby: Lobby;
  @state() private _shouldFSWidgetBeShown = false;
  @state() private _bonusIdToNavigate?: string = undefined;
  @state() private _freeSpinGames: GameDetails[] = [];
  @state() private _userSession: LoginObject | undefined;
  @state() private _allowShowingStories = true;
  @state() private _allowBestMatch = false;
  @state() private _loggedIn = false;
  @state() private _isStoriesComponentLoaded = false;
  @state() private _isBestMatchComponentLoaded = false;
  @state() private _allowFooterRendering = false;

  private readonly _clubThousandItemsLimit = 20;
  private readonly _favoritesFilterId = 'userFavorites';
  private readonly _lazyLoadedCategories: string[] = [];
  private readonly _pagePaddingInline = 'var(--page-padding-inline)';
  private readonly _subHelper = new SubHelper();

  private _favoritesTileType: TileType;
  private _firstFullRender = true;

  // Render tasks
  private _bannerTaskId: number | null = null;
  private _favoritesTaskId: number | null = null;
  private _freeSpinWidgetTaskId: number | null = null;
  private _lastPlayedTaskId: number | null = null;
  private _lobbyTaskId: number | null = null;

  connectedCallback() {
    super.connectedCallback();
    window.$app.perf.setTag('lobbyPage');
    this._bannerTaskId = window.$app.renderService.addTask('banner');
    this._freeSpinWidgetTaskId = window.$app.renderService.addTask('free-spin-widget');
    this._lastPlayedTaskId = window.$app.renderService.addTask('last-played');
    this._lobbyTaskId = window.$app.renderService.addTask('lobby');
    this._subHelper.addSub(App.instance.content.getHeroBannerStore(), (_) => this._updateBannerData(_), true);
    this._subHelper.addSub(App.instance.layoutStore, (_) => this._updateLayout(_), true);
    this._subHelper.addSub(window.$app.loginStore, (_) => this._updateSession(_), true);
    this._subHelper.addSub(App.instance.content.getLobby(), (_) => this._updateLobby(_), true);
    App.instance.login.getFinalLoginStatus().then((loginStatus) => {
      if (!this.isConnected) return;
      if (loginStatus === LoginStatus.LOGGED_IN) {
        this._initUserContent();
        this._loggedIn = true;
      } else if (loginStatus === LoginStatus.LOGGED_OUT) {
        if (this._favoritesTaskId !== null) {
          this._favoritesTaskId = window.$app.renderService.removeTask(this._favoritesTaskId);
        }
        if (this._lastPlayedTaskId !== null) {
          this._lastPlayedTaskId = window.$app.renderService.removeTask(this._lastPlayedTaskId);
        }
        if (this._freeSpinWidgetTaskId !== null) {
          this._freeSpinWidgetTaskId = window.$app.renderService.removeTask(this._freeSpinWidgetTaskId);
        }
      }
    });

    idleTick().then(() => {
      if (!this.isConnected) return;
      // Lower priority requests/updates
      this._subHelper.addSub(App.instance.content.getClubThousandItems(), (_) => this._updateClubThousand(_), true);
    });
    App.instance.trackingStore.value.gameSource = undefined;
    dispatchCustomEvent(this, HeaderOptionsEventName, {
      title: '',
      trackingSource: TrackingEventSource.LOBBY,
    });
    window.$app.features.getFeature(FeatureFlag.StoriesEnabled).then((isStoriesEnabled) => {
      if (!this.isConnected) return;
      this._allowShowingStories = !!isStoriesEnabled;
    });
    window.$app.features.getFeature(FeatureFlag.BestMatchEnabled).then((isBestMatchEnabled) => {
      if (!this.isConnected) return;
      this._allowBestMatch = !!isBestMatchEnabled;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._subHelper.unsubscribeAll();
  }

  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('_loggedIn') || changedProps.has('_allowShowingStories')) {
      if (this._loggedIn && this._allowShowingStories && !isDesktopWidth()) {
        this._subHelper.addSub(App.instance.content.getStoriesStore(), (_) => this._updateStoriesData(_), true);
        import('../../../src/_ui-core_/components/ui-stories/ui-stories').then(() => {
          this._isStoriesComponentLoaded = true;
        });
      }
    }

    if (changedProps.has('_allowBestMatch')) {
      if (this._allowBestMatch && window.$app.casinoConfig.bestMatch && !this._isBestMatchComponentLoaded) {
        import('../../_ui-core_/components/ui-best-match-banner/ui-best-match-banner').then(() => {
          this._isBestMatchComponentLoaded = true;
        });
      }
    }
  }

  render() {
    if (!this._lobby) {
      return nothing;
    }
    let order = 0;
    const a11yTitle = App.instance.appSettings.isSHCasino ? I18n.sh.a11y.heading.lobby : I18n.a11y.heading.lobby;
    return html`
      <div class="lobby-page">
        <ui-a11y-title>${window.$app.$t.get(a11yTitle)}</ui-a11y-title>
        ${this._renderExtras()}
        ${this._lobby?.categories?.map((category, i) => {
          order = i + 1;
          return this._renderCategoryUi(category, order);
        })}
        ${this._renderStudiosUi(this._lobby.studios, order + 1)}
        ${this._renderClub1000Ui(this._clubThousandItems ?? [], order + 2)}
      </div>
    `;
  }

  private _renderCarouselUi(cat: LobbyCategory, priority: FetchPriority) {
    return html`
      <ui-games-carousel
        class="carousel"
        .trackingSource=${cat.filterId ?? TrackingEventSource.LOBBY}
        .categorySlug=${cat.slug ?? TrackingEventSource.LOBBY}
        .games=${cat.games as LobbyGame[]}
        .display=${cat.display}
        .fetchPriority=${priority}
        padding=${this._pagePaddingInline}
      ></ui-games-carousel>
    `;
  }

  private _renderCategoryTitleUi({
    title,
    slug,
    order,
    count,
  }: {
    title: string;
    slug: string;
    order?: number;
    count?: number;
  }) {
    const navigateTo = (link: string) => {
      window.$app.track.navigateTo(NavigationTrigger.CATEGORY, link, true);
      if (link === CategoryPageId.STUDIOS) {
        App.instance.router.navigateToProviders();
      } else {
        App.instance.router.navigateToCategory(link);
      }
    };

    const action = {
      text: count ? `${window.$app.$t.get(I18n.base.viewAll)} (${count})` : window.$app.$t.get(I18n.base.viewAll),
      ariaLabel: count
        ? `${window.$app.$t.get(I18n.base.viewAll)}: ${title} (${count})`
        : `${window.$app.$t.get(I18n.base.viewAll)}: ${title}`,
      callback: () => navigateTo(slug),
    };

    const href =
      slug === CategoryPageId.STUDIOS
        ? App.instance.router.getPathNavigateToProviders()
        : App.instance.router.getPathNavigateToCategory(slug ?? '');

    return html`
      <ui-section-header
        class="section-header nested"
        .action=${action}
        .href=${href}
        .order=${order}
        title=${title}
      ></ui-section-header>
    `;
  }

  private _renderCategoryUi(category: LobbyCategory, order: number) {
    const isUserBased = category.isUserBased;
    const isAwaitingGames = category.games === undefined;
    const isSessionReady = this._userSession?.loginStatus === LoginStatus.LOGGED_IN;

    if (isUserBased && !isSessionReady) {
      // User based. Wait for login state
      return nothing;
    }

    if (isUserBased && isAwaitingGames && !this._lazyLoadedCategories.includes(category.filterId)) {
      // Add to queue to lazy load games
      this._lazyLoadedCategories.push(category.filterId);
      this._getGamesByFilter(category);
      return nothing;
    }

    if (category.filterId === FRONTEND_ONLY_CATEGORY.ACTIVITY_LANE) {
      if (!window.$app.appConfig.apiUrl_feed_activity.length) {
        // Skip if no feed url is available
        return nothing;
      }
      return this._renderActivityLane();
    }

    if (category.filterId === FRONTEND_ONLY_CATEGORY.USER_AREA) {
      return this._renderUserArea();
    }

    // `_userSession`, not `_loggedIn` (which latches true and never resets).
    const isLoggedIn = this._userSession?.loginStatus === LoginStatus.LOGGED_IN;

    if (category.filterId === FRONTEND_ONLY_CATEGORY.BEST_MATCH_BANNER_LOGGED_IN) {
      return isLoggedIn ? this._renderBestMatchCTA() : nothing;
    }

    if (category.filterId === FRONTEND_ONLY_CATEGORY.BEST_MATCH_BANNER_LOGGED_OUT) {
      return isLoggedIn ? nothing : this._renderBestMatchCTA();
    }

    if (!category.games?.length) {
      return nothing;
    }

    const priority = order < 2 ? FetchPriority.HIGH : FetchPriority.LOW;
    const tiles = this._renderCarouselUi(category, priority);
    const header = this._renderCategoryTitleUi({
      title: category.title,
      slug: category.slug,
      order,
      count: category.gamesTotalCount,
    });

    return html`<div
      class="category"
      data-games-count=${category.gamesTotalCount}
      data-test=${category.filterId}
      style="order: ${order}"
    >
      ${header} ${tiles}
    </div>`;
  }

  private _renderStudioCarouselUi(studios: Studio[]) {
    const sortedStudios = sortProviderByGamesNumber(studios);

    return html`
      <ui-studios-carousel
        class="carousel"
        padding=${this._pagePaddingInline}
        source="lobby"
        .studios=${sortedStudios}
      ></ui-studios-carousel>
    `;
  }

  private _renderClub1000Ui(clubThousandItems: ClubThousand[], order: number) {
    if (
      this._clubThousandItems?.length === 0 ||
      !App.instance.featureFlag.showClub1000Lane() ||
      !this._allowFooterRendering
    ) {
      return nothing;
    }

    const action = {
      text: window.$app.$t.get(I18n.base.viewAll),
      callback: () => App.instance.router.navigateToClubThousands(),
    };
    const header = html` <ui-section-header
      class="section-header nested"
      title="Club 1.000x"
      .action=${action}
      .href=${App.instance.router.getPathNavigateToClubThousands()}
    ></ui-section-header>`;

    return html`
      <div class="category" style="order: ${order}">
        ${header}
        <ui-club-1000-carousel
          class="carousel"
          padding=${this._pagePaddingInline}
          .loggedIn=${this._userSession?.loginStatus === LoginStatus.LOGGED_IN}
          .clubThousandItems=${clubThousandItems?.slice(0, this._clubThousandItemsLimit)}
        ></ui-club-1000-carousel>
      </div>
    `;
  }

  private _renderStudiosUi(studios: Studio[], order = 999) {
    if (studios.length === 0 || !this._allowFooterRendering) {
      return nothing;
    }
    const header = this._renderCategoryTitleUi({
      title: window.$app.$t.get(I18n.base.studios),
      slug: CategoryPageId.STUDIOS,
    });
    const tiles = this._renderStudioCarouselUi(studios);
    return html` <div
      class="category"
      data-test="studios"
      style="order: ${order}"
    >
      ${header}${tiles}
    </div>`;
  }

  private _renderExtras() {
    return html` ${this._renderStatusBanner()} ${this._renderHeroBanner()} ${this._renderFreeSpinWidget()} `;
  }

  private _renderBestMatchCTA() {
    if (!this._allowBestMatch || !window.$app.casinoConfig.bestMatch || !this._isBestMatchComponentLoaded) {
      return nothing;
    }
    return html`<ui-best-match-banner></ui-best-match-banner>`;
  }

  private _renderFreeSpinWidget() {
    if (!this._shouldFSWidgetBeShown) {
      return nothing;
    }
    return html`<ui-free-spin-available
      class="free-spin-widget-wrapper"
      .games=${this._freeSpinGames}
      @click=${this._handleFreeSpinWidgetClick}
    ></ui-free-spin-available>`;
  }

  private _renderActivityLane() {
    if (!App.instance.featureFlag.showActivityLane()) {
      return nothing;
    }

    return html`<ui-activity-lane class=${`activity--${this._layout}`}></ui-activity-lane>`;
  }

  private _renderStatusBanner() {
    return html`<ui-global-status-banner class="status-banner"></ui-global-status-banner>`;
  }

  private _renderHeroBanner() {
    if (!App.instance.featureFlag.showHeroBanner()) {
      return nothing;
    }

    if (isDesktopWidth()) {
      if (!this._banners?.length) {
        return nothing;
      }

      return html`
        <div class="hero-banner-wrapper desktop">
          <ui-slider-desktop .loggedIn=${this._loggedIn} .banners=${this._banners!}></ui-slider-desktop>
        </div>
      `;
    }

    if (this._loggedIn && this._allowShowingStories) {
      if (!this._stories?.length) {
        return nothing;
      }

      if (!this._isStoriesComponentLoaded) {
        return nothing;
      }

      return html`
        <div class="stories-wrapper">
          <ui-stories .stories=${this._stories}></ui-stories>
        </div>
      `;
    }

    if (!this._banners?.length) {
      return nothing;
    }

    return html`
      <div class="hero-banner-wrapper">
        <ui-slider .loggedIn=${this._loggedIn} .banners=${this._banners}></ui-slider>
      </div>
    `;
  }

  private _renderUserArea() {
    if (!this._loggedIn) {
      return nothing;
    }

    if (!this._favoriteGames?.length && !this._lastPlayedGames?.length) {
      // Neither favoriteGames nor lastPlayedGames have any items
      return nothing;
    }

    const data: UserAreaGame[] = [];

    if (this._lastPlayedGames?.length) {
      data.push({
        categoryTranslationKey: I18n.playerArea.lastPlayed,
        title: window.$app.$t.get(I18n.playerArea.lastPlayed),
        games: this._lastPlayedGames,
        id: 'lastPlayed',
      });
    }

    if (this._favoriteGames?.length) {
      window.$app.logger.log(`Rendering ${this._favoriteGames.length} favorite games in user area`);
      data.push({
        categoryTranslationKey: I18n.playerArea.favorites,
        title: window.$app.$t.get(I18n.playerArea.favorites),
        games: [...this._favoriteGames].reverse(),
        id: 'favorites',
      });
    }

    return html`
      <div class="user-area-wrapper">
        <ui-user-area .data="${data}" .trackingSource=${'lobby'}></ui-user-area>
      </div>
    `;
  }

  private _initUserContent() {
    // Fetch bonus data and subscribe to bonus store
    App.instance.bonus.getActualBonuses();
    this._subHelper.addSub(App.instance.bonus.bonusStore, (storeData) => this._updateBonus(storeData), true);

    // Subscribe to favorites store
    this._favoritesTaskId = window.$app.renderService.addTask('favorites');
    this._subHelper.addSub(App.instance.favorites.store, (favorites) => this._updateFavorites(favorites), true);

    // Get last played games of user
    App.instance.content.filterGames({ filterId: 'userLastPlayed', isUserBased: true }, TileType.P1).then((games) => {
      window.$app.logger.log('user last played games:', games.length);
      this._lastPlayedGames = games;
      if (this._lastPlayedTaskId !== null) {
        this._lastPlayedTaskId = window.$app.renderService.removeTask(this._lastPlayedTaskId);
      }
    });
  }

  private _updateBonus(bonusData: BonusStoreState) {
    if (bonusData.actualBonuses === null) {
      // data not ready
      return;
    }

    const freeSpinBonus = bonusData.actualBonuses.find(
      (b) => b.state === BonusState.ACTIVE && b.stage === BonusStage.FREE_SPIN_ACTIVE,
    );

    if (freeSpinBonus) {
      if (!this._shouldFSWidgetBeShown) {
        window.$app.track.gamificationGames({
          eventAction: 'FS_available',
          fsAvail: String(freeSpinBonus.bonusBalance.freeSpin?.count ?? 0),
        });
      }
      this._shouldFSWidgetBeShown = true;
      this._bonusIdToNavigate = freeSpinBonus.bonusId;
      this._loadFreeSpinGames(freeSpinBonus.campaignId);
    } else {
      this._shouldFSWidgetBeShown = false;
      this._freeSpinGames = [];
    }

    if (this._freeSpinWidgetTaskId !== null) {
      this._freeSpinWidgetTaskId = window.$app.renderService.removeTask(this._freeSpinWidgetTaskId);
    }
  }

  private async _loadFreeSpinGames(campaignId: string) {
    const campaigns = await App.instance.bonus.getCampaignInfoByIds([campaignId]).catch((err) => {
      window.$app.logger.warn('Failed to load free spin campaign', err);
      return null;
    });

    const providerCampaign = campaigns?.[0]?.externalConfig?.providerCampaign;
    if (!providerCampaign?.games?.length) return;

    const results = await Promise.all(
      providerCampaign.games.map((gameId) => getGame(gameId, providerCampaign.integration)),
    );
    this._freeSpinGames = results.filter((game): game is GameDetails => game !== null);
  }

  private _emptyCategory(filterId: string) {
    window.$app.logger.log(`category '${filterId}' is empty`);
    const categoryIndex = this._lobby.categories.findIndex((_) => _.filterId === filterId);
    if (categoryIndex !== -1) {
      this._lobby.categories[categoryIndex]!.games = [];
    }
  }

  /**
   * Filter categories without games and keep user based categories that need to be fetched.
   */
  private _filterEmptyAndUnloadedCategories(categories: LobbyCategory[]) {
    return categories.filter((category) => {
      const isAwaitingGames = category.games === undefined;
      const hasAvailableGames = this._hasGames(category);
      const isUserBased = category.isUserBased;
      if (isUserBased && isAwaitingGames) {
        // Keep user based category that has not been fetched
        window.$app.logger.log(`await user based category '${category.filterId}' ('${category.title}')…`);
        return true;
      }
      if (!(isAwaitingGames || hasAvailableGames)) {
        if (category.filterId === this._favoritesFilterId) {
          // Always keep favorites as user might add a game in the lobby
          return true;
        }
        // Remove empty category. Can also be a user based category that has no games.
        window.$app.logger.log(`remove empty category '${category.filterId}' ('${category.title}')`);
        return false;
      }
      return true;
    });
  }

  private async _getGamesByFilter(category: LobbyCategory) {
    // Raise the limit beyond 20 for the total game count.
    const limit = 2_000;
    const games = await App.instance.content.filterGames(category, category.display.tile, limit);
    window.$app.logger.log(`get games by filter '${category.filterId}' ('${category.title}'):`, games);
    if (games.length > 0) {
      category.gamesTotalCount = games.length;
      this._updateGames(category.filterId, games);
    } else {
      this._emptyCategory(category.filterId);
    }
  }

  private _hasGames(category: LobbyCategory): boolean {
    return category.games !== undefined && category.games.length > 0;
  }

  private async _updateGames(filterId: string, games: LobbyGame[]) {
    if (this._lobby === undefined || this._lobby.categories === undefined) {
      window.$app.logger.log(`Lobby or categories undefined. Cannot update games of filter '${filterId}'`);
      return;
    }
    window.$app.logger.log(`update games of filter '${filterId}':`, games);
    const categoryIndex = this._lobby.categories.findIndex((_) => _.filterId === filterId);
    if (categoryIndex === -1) {
      // filterId not available
      return;
    }

    // Add games to category
    this._lobby.categories[categoryIndex] = {
      ...this._lobby.categories[categoryIndex]!,
      games,
    };

    this._lobby.categories = this._filterEmptyAndUnloadedCategories(this._lobby.categories);
    window.$app.logger.log('categories:', this._lobby.categories);

    // Trigger update
    this._lobby = { ...this._lobby };

    if ((await App.instance.login.getFinalLoginStatus()) === LoginStatus.LOGGED_IN) {
      this._trackGamesImpression();
    }
  }

  private async _updateFavorites(favorites: Set<string> | undefined): Promise<void> {
    if (this._favoritesTaskId !== null) {
      this._favoritesTaskId = window.$app.renderService.removeTask(this._favoritesTaskId);
    }

    if (!favorites?.size) {
      window.$app.logger.log('No favorite games available. Clear favorites in lobby.');
      this._favoriteGames = [];
      this._updateGames(this._favoritesFilterId, []);
      return;
    }

    const games = await App.instance.content.listGames([...favorites], this._favoritesTileType);

    this._updateGames(this._favoritesFilterId, games);
    this._favoriteGames = games;
    window.$app.logger.log('lobby._updateFavorites: favorite games:', this._favoriteGames);

    if (!games.length) {
      window.$app.logger.log('No favorite games available in the current lobby. Favorites are not shown.');
    }
  }

  private _updateClubThousand(clubThousandItems: ClubThousand[] | undefined) {
    window.$app.logger.log('club 1000 updated scheduled');
    // Delay update to allow for paint
    idleTick().then(() => {
      window.$app.logger.log('club 1000 updated');
      this._clubThousandItems = clubThousandItems;
    });
  }

  private _updateBannerData(data: HeroBanner | undefined) {
    if (this._bannerTaskId !== null) {
      this._bannerTaskId = window.$app.renderService.removeTask(this._bannerTaskId);
    }
    if (data === undefined || data.length === 0) {
      this._banners = [];
      return;
    }

    const hasUserLoggedInOnce = localStorage_getOrNull(STORAGE_KEY_HAS_USER_LOGGED_IN_ONCE);
    let banners = data;

    if (hasUserLoggedInOnce) {
      banners = data.filter((banner) => !banner.is_welcome_banner);
    }

    this._banners = banners;
  }

  private _updateStoriesData(data: Story[] | undefined) {
    if (this._bannerTaskId !== null) {
      this._bannerTaskId = window.$app.renderService.removeTask(this._bannerTaskId);
    }
    if (data === undefined || data.length === 0) {
      this._stories = [];
      return;
    }

    this._stories = data;
  }

  private _updateLayout(layout: Layout) {
    this._layout = layout;
  }

  private async _updateLobby(lobby: Lobby | undefined) {
    window.$app.logger.log('lobby updated');
    if (lobby === undefined) {
      window.$app.logger.warn('lobby undefined');
      if (this._lobbyTaskId !== null) {
        this._lobbyTaskId = window.$app.renderService.removeTask(this._lobbyTaskId);
      }
      return;
    }

    if (lobby.categories === undefined) {
      window.$app.logger.warn('lobby has no categories!');
      if (this._lobbyTaskId !== null) {
        this._lobbyTaskId = window.$app.renderService.removeTask(this._lobbyTaskId);
      }
      return;
    }

    if (this._userSession === undefined) {
      // Remove user based categories
      lobby.categories = filterUserBasedCategories(lobby.categories);
    }

    lobby.categories = this._filterEmptyAndUnloadedCategories(lobby.categories);

    lobby.categories = adaptCategoriesToLayout(lobby.categories, this._layout!);

    // Get and store tile type of favorites
    const favoritesCategoryIndex = lobby.categories.findIndex((_) => _.filterId === this._favoritesFilterId);
    if (favoritesCategoryIndex !== -1) {
      this._favoritesTileType = lobby.categories[favoritesCategoryIndex]!.display.tile;
    }

    if (this._favoriteGames.length) {
      window.$app.logger.log('Update favorite games in lobby');
      this._updateGames(this._favoritesFilterId, this._favoriteGames);
    }

    if (this._lobbyTaskId !== null) {
      window.$app.logger.log('lobby: remove render task');
      this._lobbyTaskId = window.$app.renderService.removeTask(this._lobbyTaskId);
    }

    const BATCH_SIZE = 3;

    if (lobby.categories.length > BATCH_SIZE) {
      // Progressive rendering: render categories in batches to avoid a single
      // long task that blocks the main thread for 150ms+ during performUpdate.
      for (let i = 0; i < lobby.categories.length; i += BATCH_SIZE) {
        this._lobby = { ...lobby, categories: lobby.categories.slice(0, i + BATCH_SIZE) };
        if (i + BATCH_SIZE < lobby.categories.length) {
          await yieldToMain();
          if (!this.isConnected) return;
        }
      }
    } else {
      this._lobby = { ...lobby };
    }

    if (this._firstFullRender) {
      // Wait for lobby to load before rendering footer assets
      this._allowFooterRendering = true;
      // Dispatch event to restore scroll position
      this._firstFullRender = false;
      dispatchCustomEvent(this, 'restore-scroll-position');
    }
  }

  private _updateSession(session: LoginObject) {
    if (session.loginStatus === LoginStatus.LOGGED_OUT) {
      this._userSession = undefined;
    } else {
      this._userSession = { ...session };
    }
  }

  private _handleFreeSpinWidgetClick() {
    window.$app.track.customEvent({
      event: TrackingEventSource.FS_WIDGET,
      eventAction: TrackableEventAction.CLICK,
    });

    if (this._bonusIdToNavigate) {
      App.instance.router.navigateToIncentiveReward(this._bonusIdToNavigate);
    } else {
      App.instance.router.navigateToIncentive();
    }
  }

  private _trackGamesImpression() {
    const mappedCategories = this._lobby.categories.map((category) => ({
      location: category.slug,
      gameSlugs: category.games ? category.games.map((game) => game.slug) : [''],
    }));

    window.$app.vaix.trackGamesImpressionVaix(mappedCategories, true);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: LobbyPage;
  }
}
